import TableHeader from "@commons/Table/TableHeader";
import Table from "@commons/Table/TableContent";
import AddIcon from "@mui/icons-material/Add";
import { usePage } from "@hooks/usePage";
import { config } from "@/config";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { collarColumns } from "@typings/entities/devices/collar/table";
import { useGetCollars } from "@services/device/collar/list";
import {
  prepareCollarsTable,
  type CollarWithLabels,
} from "@utils/translator/device/collar";
import { isEmpty } from "@utils/formatters";
import { useNavigate } from "react-router-dom";
import { useExportCollars } from "@hooks/exports/useExportCollars";
import { usePermissions } from "@hooks/usePermissions";

function TableCollar({ filter }: { filter?: string }) {
  const { openModal, setDataModal } = usePage();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);
  const { can } = usePermissions();

  const { handleExport, isLoading: isExporting } = useExportCollars();

  const navigate = useNavigate();

  const heads = collarColumns;

  const filters = {
    number: !isEmpty(filter) ? Number(filter) : undefined,
    limit: rowsPerPage,
    page: page + 1,
  };

  const { error, isLoading, data: collars } = useGetCollars(filters);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  const processedCollars = prepareCollarsTable(collars?.items || []);

  useEffect(() => {
    if (error) {
      toast.error("Erro ao carregar os Rastreadores");
    }
  }, [error]);

  if (!processedCollars) {
    return null;
  }

  const handleEdit = (collar: CollarWithLabels) => {
    setDataModal({ id: collar.id });
    openModal("editCollar");
  };

  const canCreateCollar = can("create", "collars");
  const canUpdateCollar = can("update", "collars");

  return (
    <div className="w-full flex flex-col justify-center items-center text-center bg-white rounded-lg shadow-md p-4">
      <TableHeader.Root>
        {canCreateCollar && (
          <TableHeader.Button
            customVariant="secondary"
            startIcon={<AddIcon />}
            onClick={() => openModal("newCollar")}
            className="w-full xs:w-auto"
          >
            Novo Rastreador
          </TableHeader.Button>
        )}
        <TableHeader.Button
          customVariant="dark"
          className="w-full xs:w-auto"
          onClick={() => handleExport()}
          disabled={isExporting}
        >
          {isExporting ? "Exportando..." : "Exportar .csv"}
        </TableHeader.Button>
      </TableHeader.Root>

      <Table.Root isLoading={isLoading}>
        <Table.Head heads={heads} actions={true} />
        <Table.Body
          rows={processedCollars}
          columns={heads.map((head) => head.id)}
          actions={canUpdateCollar ? ["view", "edit"] : ["view"]}
          onView={(collar) => navigate(`/devices/collar/${collar.id}`)}
          onEdit={canUpdateCollar ? handleEdit : undefined}
        />
        <Table.Footer
          colSpan={heads.length}
          count={collars?.meta.totalItems ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Table.Root>
    </div>
  );
}

export default TableCollar;
